/**
 * SpendLimitsCard — every spend limit of the workspace (Settings):
 *
 *   - the DAILY AD LIMIT — required; nothing deploys or resumes without one
 *     (DailyAdLimitCard);
 *   - the MONTHLY CAP — optional: once this month's ad spend across every ad
 *     account reaches it, everything pauses until next month;
 *   - the WALLET CAPS — optional, managed ad accounts only (shown when the
 *     workspace has an ad wallet): the same, per day and per month, for the
 *     spend the wallet pays for.
 *
 * Only a workspace owner or admin can change them, from here — the server
 * refuses API keys, scheduled actions and every AI surface, and checks the
 * role again. Adding or lowering a cap needs nothing more; RAISING one needs
 * the amount typed a second time, and REMOVING one needs the removal word
 * typed. Everyone else sees them read-only.
 *
 * $0 is a valid cap: no spend at all in its scope ("No cap" is the absence of
 * one). Setting $0 is an add or a lowering and stops what it covers at once;
 * raising from $0 needs the amount typed again.
 */

import { useState } from 'react';
import { ApiError, type AdoptSpendCapParam } from '@/lib/api';
import { useAdoptSpendCaps, usePutAdoptSpendCap } from '../hooks/useAdvertisingOptimization';
import { DailyAdLimitCard, parseUsdToCents } from './DailyAdLimitCard';

function usd(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function errorText(error: unknown): string {
  if (error instanceof ApiError) {
    switch (error.code) {
      case 'CONFIRMATION_REQUIRED':
        return error.message;
      case 'HUMAN_SESSION_REQUIRED':
        return 'Spend caps can only be changed from the Fias web app while signed in.';
      case 'OWNER_OR_ADMIN_REQUIRED':
        return 'Only a workspace owner or admin can change a spend cap.';
      case 'NO_AD_WALLET':
        return 'Wallet caps apply to managed ad accounts, and this workspace has no ad wallet.';
      default:
        return error.message;
    }
  }
  return error instanceof Error ? error.message : 'Saving the cap failed';
}

interface CapRowProps {
  holderId: string;
  cap: AdoptSpendCapParam;
  title: string;
  description: string;
  /** What a $0 cap means for this cap ("No ads can run while …"). */
  zeroMessage: string;
  /** "month" or "day". */
  period: 'month' | 'day';
  capCents: number | null;
  updatedAt: string | null;
  /** What was spent so far this period, as last read; null = not read yet. */
  spent: { cents: number; observedAt: string; fresh: boolean } | null;
  bounds: { minCents: number; maxCents: number };
  canEdit: boolean;
  removeWord: string;
}

/** One optional cap: its value, what was spent against it, and the controls. */
export function OptionalSpendCapRow(props: CapRowProps) {
  const { cap, title, period, capCents, spent, bounds, canEdit, removeWord, zeroMessage } = props;
  const putCap = usePutAdoptSpendCap(props.holderId);
  const [amount, setAmount] = useState('');
  const [confirm, setConfirm] = useState('');
  const [removing, setRemoving] = useState(false);
  const [removeTyped, setRemoveTyped] = useState('');
  const slug = `adopt-cap-${cap}`;
  const per = period === 'month' ? '/month' : '/day';
  const spentLabel = period === 'month' ? 'Spent this month' : 'Spent today';

  const newCents = parseUsdToCents(amount);
  const inBounds = newCents !== null && newCents >= bounds.minCents && newCents <= bounds.maxCents;
  const isRaise = newCents !== null && capCents !== null && newCents > capCents;
  const unchanged = newCents !== null && newCents === capCents;
  const confirmMatches = newCents !== null && parseUsdToCents(confirm) === newCents;
  // $0 stops what the cap covers whatever was spent (nothing is below $0).
  const setsZero = newCents === 0 && !unchanged;
  const stopsNow =
    newCents !== null &&
    !isRaise &&
    !unchanged &&
    (newCents === 0 || (spent !== null && newCents <= spent.cents));
  const canSave =
    canEdit && inBounds && !unchanged && (!isRaise || confirmMatches) && !putCap.isPending;

  const reset = (): void => {
    setAmount('');
    setConfirm('');
    setRemoving(false);
    setRemoveTyped('');
  };
  const save = (): void => {
    if (newCents === null) return;
    putCap.mutate(
      {
        cap,
        body: { capCents: newCents, ...(isRaise ? { confirmCents: newCents } : {}) },
      },
      { onSuccess: reset },
    );
  };
  const remove = (): void => {
    putCap.mutate(
      { cap, body: { capCents: null, confirmRemoval: removeTyped } },
      { onSuccess: reset },
    );
  };

  const result = putCap.data;

  return (
    <div
      aria-labelledby={`${slug}-title`}
      role="group"
      className="flex flex-col gap-2 border-t border-border pt-3"
    >
      <div>
        <h3 id={`${slug}-title`} className="text-sm font-medium">
          {title}
        </h3>
        <p className="text-xs text-muted-foreground">{props.description}</p>
      </div>

      <dl
        className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1 text-sm"
        data-testid={`${slug}-current`}
      >
        <dt className="text-muted-foreground">Cap</dt>
        <dd>
          {capCents === null ? (
            <span className="text-muted-foreground">No cap</span>
          ) : (
            <>
              <span className="font-semibold">
                {usd(capCents)}
                {per}
              </span>
              {props.updatedAt ? (
                <span className="text-muted-foreground">
                  {' '}
                  · last changed {new Date(props.updatedAt).toLocaleString()}
                </span>
              ) : null}
              {capCents === 0 ? (
                <span className="block text-amber-700" data-testid={`${slug}-zero`}>
                  {zeroMessage}
                </span>
              ) : null}
            </>
          )}
        </dd>
        <dt className="text-muted-foreground">{spentLabel}</dt>
        <dd data-testid={`${slug}-spent`}>
          {spent
            ? `${usd(spent.cents)} (as of ${new Date(spent.observedAt).toLocaleTimeString()}${spent.fresh ? '' : ', may be out of date'})`
            : 'Not read yet'}
        </dd>
      </dl>

      {canEdit ? (
        <div className="flex flex-col gap-2">
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-muted-foreground">
              {capCents === null ? `${title.replace(/ \(optional\)$/, '')} (USD)` : 'New cap (USD)'}
            </span>
            <input
              type="text"
              inputMode="decimal"
              className="w-40 rounded-md border border-border bg-background px-2 py-1.5"
              value={amount}
              onChange={(e) => {
                setAmount(e.target.value);
                setConfirm('');
              }}
              placeholder={period === 'month' ? '300.00' : '20.00'}
            />
          </label>
          {amount.trim() !== '' && !inBounds && (
            <p className="text-xs text-rose-600">
              Enter an amount between {usd(bounds.minCents)} and {usd(bounds.maxCents)}.
            </p>
          )}
          {inBounds && isRaise && (
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-muted-foreground">Type the new amount to confirm</span>
              <input
                type="text"
                inputMode="decimal"
                autoComplete="off"
                className="w-40 rounded-md border border-border bg-background px-2 py-1.5"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
              />
            </label>
          )}
          {inBounds && stopsNow && setsZero && (
            <p className="text-xs text-amber-700">
              {zeroMessage} Every running placement it covers will be paused now, and stays paused
              until the cap is raised or removed.
            </p>
          )}
          {inBounds && stopsNow && !setsZero && (
            <p className="text-xs text-amber-700">
              This is at or below what has already been spent ({usd(spent!.cents)}), so every
              running placement it covers will be paused now, until next {period}.
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={save}
              disabled={!canSave}
              className="px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-sm disabled:opacity-50"
            >
              {capCents === null ? 'Add cap' : isRaise ? 'Raise cap' : 'Lower cap'}
            </button>
            {capCents !== null && !removing && (
              <button
                onClick={() => setRemoving(true)}
                className="px-3 py-1.5 rounded-md border border-border text-sm"
              >
                Remove cap
              </button>
            )}
          </div>
          {capCents !== null && removing && (
            <div className="flex flex-col gap-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-sm text-amber-900">
              <label className="flex flex-col gap-1">
                <span>
                  {capCents === 0
                    ? 'Removing the cap lets the ads it covers spend again, with no cap. '
                    : `Removing the cap lets spend continue past ${usd(capCents)}${per}. `}
                  Type {removeWord} to confirm.
                </span>
                <input
                  type="text"
                  autoComplete="off"
                  className="w-40 rounded-md border border-border bg-background px-2 py-1.5"
                  value={removeTyped}
                  onChange={(e) => setRemoveTyped(e.target.value)}
                />
              </label>
              <div className="flex gap-2">
                <button
                  onClick={remove}
                  disabled={removeTyped !== removeWord || putCap.isPending}
                  className="px-3 py-1.5 rounded-md bg-rose-600 text-white text-sm disabled:opacity-50"
                >
                  Confirm removal
                </button>
                <button
                  onClick={() => {
                    setRemoving(false);
                    setRemoveTyped('');
                  }}
                  className="px-3 py-1.5 rounded-md border border-border text-sm"
                >
                  Keep the cap
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">
          Only a workspace owner or admin can change this cap.
        </p>
      )}

      {putCap.isError && <p className="text-sm text-rose-600">{errorText(putCap.error)}</p>}
      {result && putCap.isSuccess && (
        <p className="text-sm text-emerald-700" role="status">
          {result.change.direction === 'unchanged'
            ? 'The cap is unchanged.'
            : result.change.capCents === null
              ? 'Cap removed.'
              : result.change.capCents === 0
                ? `Saved: $0${per}. ${zeroMessage}`
                : `Saved: ${usd(result.change.capCents)}${per}.`}
          {result.change.pausedPlacements > 0
            ? ` ${result.change.pausedPlacements} running placement${result.change.pausedPlacements === 1 ? ' was' : 's were'} paused.`
            : ''}
          {result.channelPause.some((c) => !c.ok)
            ? ' Google has not confirmed every pause yet; it is retried automatically.'
            : ''}
          {result.ownerNotification.sent > 0 ? ' Workspace owners were emailed.' : ''}
        </p>
      )}
    </div>
  );
}

export function SpendLimitsCard({ holderId }: { holderId: string }) {
  const capsQuery = useAdoptSpendCaps(holderId);
  const caps = capsQuery.data;
  const reading = caps?.reading ?? null;
  const spent = (cents: number | undefined) =>
    reading && cents !== undefined
      ? { cents, observedAt: reading.observedAt, fresh: reading.fresh }
      : null;

  return (
    <section
      aria-labelledby="adopt-spend-limits-title"
      className="rounded-lg border border-border p-4 flex flex-col gap-4"
    >
      <div>
        <h2 id="adopt-spend-limits-title" className="font-medium">
          Spend limits
        </h2>
        <p className="text-sm text-muted-foreground">
          What this workspace&apos;s ads may spend. Only a workspace owner or admin can change
          these, here — never an AI assistant, an API key or a scheduled action — and every change
          is emailed to the workspace&apos;s owners.
        </p>
      </div>

      <DailyAdLimitCard holderId={holderId} />

      {capsQuery.isLoading && <p className="text-sm text-muted-foreground">Loading spend caps…</p>}
      {caps && (
        <OptionalSpendCapRow
          holderId={holderId}
          cap="monthly"
          title="Monthly cap (optional)"
          description="Once this month's ad spend across all of the workspace's ad accounts reaches the cap, every running placement pauses until next month (each ad account's time zone)."
          zeroMessage="No ads can run while the monthly cap is $0."
          period="month"
          capCents={caps.monthly.capCents}
          updatedAt={caps.monthly.updatedAt}
          spent={spent(reading?.monthCents)}
          bounds={caps.monthly.bounds}
          canEdit={caps.canEdit}
          removeWord={caps.removeConfirmation}
        />
      )}
      {caps?.wallet && (
        <div className="flex flex-col gap-2" data-testid="adopt-wallet-caps">
          <p className="text-sm font-medium">Ad wallet caps (managed ad accounts)</p>
          <OptionalSpendCapRow
            holderId={holderId}
            cap="wallet-daily"
            title="Wallet daily cap (optional)"
            description="Once today's spend on the managed ad accounts reaches the cap, their placements pause until tomorrow. Counts ad spend before the 15% management fee."
            zeroMessage="No ads can run on the managed ad accounts while the wallet daily cap is $0."
            period="day"
            capCents={caps.wallet.dailyCapCents}
            updatedAt={caps.wallet.updatedAt}
            spent={spent(reading?.managedTodayCents)}
            bounds={caps.wallet.bounds.daily}
            canEdit={caps.canEdit}
            removeWord={caps.removeConfirmation}
          />
          <OptionalSpendCapRow
            holderId={holderId}
            cap="wallet-monthly"
            title="Wallet monthly cap (optional)"
            description="Once this month's spend on the managed ad accounts reaches the cap, their placements pause until next month. Counts ad spend before the 15% management fee."
            zeroMessage="No ads can run on the managed ad accounts while the wallet monthly cap is $0."
            period="month"
            capCents={caps.wallet.monthlyCapCents}
            updatedAt={caps.wallet.updatedAt}
            spent={spent(reading?.managedMonthCents)}
            bounds={caps.wallet.bounds.monthly}
            canEdit={caps.canEdit}
            removeWord={caps.removeConfirmation}
          />
        </div>
      )}
    </section>
  );
}
